import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart2, Calendar, RefreshCw, ChevronUp, ChevronDown,
  ChevronsUpDown, Search, X, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { getRelayAnalytics, getAnalyticsTrend } from '@/lib/api';

const BarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg text-xs">
      <p className="font-semibold mb-1">{d?.name}</p>
      <p className="text-green-500">Açılma: {d?.on_count} kez</p>
      <p className="text-red-500">Kapanma: {d?.off_count} kez</p>
      <p className="text-muted-foreground">Toplam: {d?.total}</p>
    </div>
  );
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return <ChevronsUpDown size={13} className="text-muted-foreground/50" />;
  return sortDir === 'asc' ? <ChevronUp size={13} className="text-primary" /> : <ChevronDown size={13} className="text-primary" />;
};

const MiniBar = ({ value, max, color }) => (
  <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
    <div className="h-full rounded-full transition-all" style={{ width: max > 0 ? `${(value / max) * 100}%` : '0%', backgroundColor: color }} />
  </div>
);

const COLS = [
  { key: 'relay_number', label: '#',       sortable: true },
  { key: 'name',         label: 'Röle Adı',sortable: true },
  { key: 'current_state',label: 'Durum',   sortable: true },
  { key: 'on_count',     label: 'Açılma',  sortable: true },
  { key: 'off_count',    label: 'Kapanma', sortable: true },
  { key: 'total',        label: 'Toplam',  sortable: true },
  { key: 'bar',          label: 'Oran',    sortable: false },
];

const Analytics = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate]       = useState(today);
  const [data, setData]       = useState([]);
  const [trend7, setTrend7]   = useState([]);
  const [trend30, setTrend30] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [search, setSearch]   = useState('');
  const [sortCol, setSortCol] = useState('relay_number');
  const [sortDir, setSortDir] = useState('asc');
  const [showAll, setShowAll] = useState(false);

  const loadDaily = async (d) => {
    setLoading(true);
    try { const res = await getRelayAnalytics(d); setData(res.data); }
    catch { toast.error('Günlük veriler yüklenemedi'); }
    finally { setLoading(false); }
  };

  const loadTrend = async () => {
    setLoadingTrend(true);
    try {
      const [r7, r30] = await Promise.all([getAnalyticsTrend(7), getAnalyticsTrend(30)]);
      setTrend7(r7.data); setTrend30(r30.data);
    } catch { toast.error('Trend veriler yüklenemedi'); }
    finally { setLoadingTrend(false); }
  };

  useEffect(() => { loadDaily(today); loadTrend(); }, []);

  const handleSort = (col) => {
    if (!COLS.find(c => c.key === col)?.sortable) return;
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const maxTotal = Math.max(...data.map(r => r.total), 1);
  const totalEvents = data.reduce((a, b) => a + b.total, 0);
  const activeRelays = data.filter(r => r.current_state).length;
  const mostActive = data.reduce((a, b) => b.total > a.total ? b : a, data[0] || {});

  const filtered = useMemo(() => {
    let rows = showAll ? data : data.filter(r => r.total > 0);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r => r.name.toLowerCase().includes(q) || String(r.relay_number).includes(q));
    }
    return [...rows].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (typeof av === 'boolean') { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      if (typeof av === 'string')  { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      return (av < bv ? -1 : av > bv ? 1 : 0) * (sortDir === 'asc' ? 1 : -1);
    });
  }, [data, search, sortCol, sortDir, showAll]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Analitik</h1>
          <p className="text-muted-foreground text-sm mt-1">Röle aktivite istatistikleri</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Bugün Toplam Event</p>
          <p className="text-2xl font-bold mt-1" style={{fontFamily:'Outfit,sans-serif'}}>{totalEvents}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Şu An Aktif Röle</p>
          <p className="text-2xl font-bold mt-1 text-green-500" style={{fontFamily:'Outfit,sans-serif'}}>
            {activeRelays}<span className="text-base text-muted-foreground font-normal">/16</span>
          </p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">En Aktif Röle (bugün)</p>
          <p className="text-2xl font-bold mt-1" style={{fontFamily:'Outfit,sans-serif'}}>
            {mostActive?.total > 0 ? `R${mostActive.relay_number}` : '—'}
          </p>
          {mostActive?.total > 0 && <p className="text-xs text-muted-foreground">{mostActive.total} event</p>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="daily">
        <TabsList className="mb-4">
          <TabsTrigger value="daily" data-testid="tab-daily">Günlük</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">7 Günlük Trend</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">30 Günlük Trend</TabsTrigger>
        </TabsList>

        {/* ── Günlük ── */}
        <TabsContent value="daily">
          <div className="bg-card border border-border rounded-xl p-5 mb-6" data-testid="analytics-chart">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-sm">Röle Toggle Sayısı</h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-muted rounded-lg px-2 py-1">
                  <Calendar size={13} className="text-muted-foreground" />
                  <Input type="date" value={date} max={today}
                    onChange={e => { setDate(e.target.value); loadDaily(e.target.value); }}
                    className="border-0 p-0 h-auto w-32 text-xs bg-transparent focus-visible:ring-0"
                    data-testid="analytics-date-input" />
                </div>
                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => loadDaily(date)} data-testid="refresh-analytics-btn">
                  <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>
            {loading ? <Skeleton className="h-56 rounded-lg" /> : totalEvents === 0 ? (
              <div className="h-56 flex items-center justify-center text-muted-foreground">
                <div className="text-center"><BarChart2 size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">Bu tarihte aktivite yok</p></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="relay_number" tickFormatter={v => `R${v}`} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<BarTooltip />} />
                  <Legend formatter={v => v === 'on_count' ? 'Açılma' : 'Kapanma'} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="on_count"  fill="#10b981" radius={[3,3,0,0]} name="on_count"  />
                  <Bar dataKey="off_count" fill="#ef4444" radius={[3,3,0,0]} name="off_count" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* DataTable */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="analytics-table">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border flex-wrap">
              <p className="font-semibold text-sm">Detay Tablosu</p>
              <div className="flex items-center gap-2 flex-1 min-w-0 max-w-xs">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Röle ara..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-sm" data-testid="table-search-input" />
                  {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X size={13} /></button>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setShowAll(v => !v)} className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${showAll ? 'bg-primary/10 text-primary border-primary/30' : 'border-border hover:bg-muted text-muted-foreground'}`} data-testid="show-all-toggle">
                  {showAll ? `Tümü (16)` : `Aktif (${data.filter(r => r.total > 0).length})`}
                </button>
                <span className="text-xs text-muted-foreground">{filtered.length} kayıt</span>
              </div>
            </div>
            {loading ? (
              <div className="p-4 space-y-2">{Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">{search ? `"${search}" bulunamadı` : 'Aktivite yok'}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {COLS.map(col => (
                      <TableHead key={col.key} onClick={() => col.sortable && handleSort(col.key)}
                        className={`text-xs font-semibold whitespace-nowrap ${col.sortable ? 'cursor-pointer select-none hover:text-foreground' : ''}`}>
                        <div className="flex items-center gap-1">{col.label}{col.sortable && <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />}</div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.relay_number} className="hover:bg-muted/40" data-testid={`analytics-row-${r.relay_number}`}>
                      <TableCell><div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold font-mono-tech ${r.current_state ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>{r.relay_number}</div></TableCell>
                      <TableCell className="font-medium text-sm">{r.name}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${r.current_state ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-muted text-muted-foreground border-border'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 inline-block ${r.current_state ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                          {r.current_state ? 'Açık' : 'Kapalı'}
                        </Badge>
                      </TableCell>
                      <TableCell><div className="flex items-center gap-2"><span className="text-green-600 dark:text-green-400 font-semibold text-sm w-5 text-right">{r.on_count}</span><MiniBar value={r.on_count} max={maxTotal} color="#10b981" /></div></TableCell>
                      <TableCell><div className="flex items-center gap-2"><span className="text-red-500 font-semibold text-sm w-5 text-right">{r.off_count}</span><MiniBar value={r.off_count} max={maxTotal} color="#ef4444" /></div></TableCell>
                      <TableCell><Badge variant="outline" className="font-mono-tech text-xs">{r.total}</Badge></TableCell>
                      <TableCell>
                        <div className="w-24">
                          <p className="text-[10px] text-muted-foreground mb-1">{r.total > 0 ? Math.round((r.on_count / r.total) * 100) : 0}% AÇ</p>
                          <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                            <div className="bg-green-500 h-full" style={{ width: r.total > 0 ? `${(r.on_count / r.total) * 100}%` : '0%' }} />
                            <div className="bg-red-500 h-full" style={{ width: r.total > 0 ? `${(r.off_count / r.total) * 100}%` : '0%' }} />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!loading && filtered.length > 0 && (
              <div className="px-4 py-2.5 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>{filtered.length} röle</span><span>Toplam {totalEvents} event • {date}</span>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── 7 Günlük Trend ── */}
        <TabsContent value="weekly">
          <TrendChart data={trend7} days={7} loading={loadingTrend} onRefresh={loadTrend} />
        </TabsContent>

        {/* ── 30 Günlük Trend ── */}
        <TabsContent value="monthly">
          <TrendChart data={trend30} days={30} loading={loadingTrend} onRefresh={loadTrend} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const TrendChart = ({ data, days, loading, onRefresh }) => {
  const totalSum = data.reduce((a, b) => a + b.total, 0);
  const maxDay = data.reduce((a, b) => b.total > a.total ? b : a, data[0] || {});

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="trend-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-primary" />
          <h2 className="font-semibold text-sm">Son {days} Gün — Toplam Event Trendi</h2>
        </div>
        <Button variant="outline" size="icon" className="h-7 w-7" onClick={onRefresh}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Mini stats */}
      <div className="flex gap-4 mb-4">
        <div className="bg-muted rounded-lg px-3 py-2">
          <p className="text-xs text-muted-foreground">Toplam Event</p>
          <p className="font-bold">{totalSum}</p>
        </div>
        <div className="bg-muted rounded-lg px-3 py-2">
          <p className="text-xs text-muted-foreground">En Yoğun Gün</p>
          <p className="font-bold">{maxDay?.date?.slice(5) || '—'} ({maxDay?.total || 0})</p>
        </div>
        <div className="bg-muted rounded-lg px-3 py-2">
          <p className="text-xs text-muted-foreground">Günlük Ortalama</p>
          <p className="font-bold">{data.length > 0 ? (totalSum / data.length).toFixed(1) : 0}</p>
        </div>
      </div>

      {loading ? <Skeleton className="h-64 rounded-lg" /> : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="date" tickFormatter={v => v.slice(5)} tick={{ fontSize: 10 }} interval={days === 7 ? 0 : 4} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<TrendTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="total"     stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} name="Toplam"   />
            <Line type="monotone" dataKey="on_count"  stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} name="Açılma"   />
            <Line type="monotone" dataKey="off_count" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} name="Kapanma"  />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default Analytics;
